(() => {
  'use strict';
  if (!window.customElements || customElements.get('fadlash-review-highlights')) return;

  class FadlashReviewHighlights extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.track = this.querySelector('[data-frh-track]');
      this.dialog = this.querySelector('dialog');
      if (!this.track || !this.dialog) return;
      this.previous = this.querySelector('[data-frh-prev]');
      this.next = this.querySelector('[data-frh-next]');
      this.image = this.querySelector('[data-frh-gallery-image]');
      this.range = this.querySelector('[data-frh-range]');
      this.groupButtons = [...this.querySelectorAll('[data-frh-group]')].filter(button => {
        const count = this.querySelectorAll('[data-review-group="' + button.dataset.frhGroup + '"]').length;
        const available = count > 0;
        const countLabel = button.querySelector('[data-frh-group-count]');
        if (countLabel) countLabel.textContent = '(' + count + ')';
        button.hidden = !available;
        return available;
      });
      this.querySelector('[data-frh-categories]').hidden = this.groupButtons.length < 2;
      this.querySelector('[data-frh-category-label]').hidden = this.groupButtons.length > 1;
      this.querySelector('[data-frh-controls]').hidden = false;
      this.on(this, 'click', event => {
        const group = event.target.closest('[data-frh-group]');
        if (group) this.selectGroup(group.dataset.frhGroup);
        const photo = event.target.closest('[data-frh-photo]');
        if (photo && this.openGallery(photo)) event.preventDefault();
        if (event.target.closest('[data-frh-prev]')) this.moveTrack(-1);
        if (event.target.closest('[data-frh-next]')) this.moveTrack(1);
        if (event.target.closest('[data-frh-close]')) this.closeGallery();
        if (event.target.closest('[data-frh-image-prev]')) this.showImage(this.imageIndex - 1);
        if (event.target.closest('[data-frh-image-next]')) this.showImage(this.imageIndex + 1);
      });
      this.on(this.track, 'scroll', () => this.updateTrack(), { passive: true });
      this.on(this.querySelector('[data-frh-categories]'), 'keydown', event => {
        const index = this.groupButtons.indexOf(event.target);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.groupButtons.length - 1 :
          (index + (event.key === 'ArrowRight' ? 1 : -1) + this.groupButtons.length) % this.groupButtons.length;
        const button = this.groupButtons[next];
        this.selectGroup(button.dataset.frhGroup); button.focus();
      });
      this.on(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.moveTrack(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.on(this.dialog, 'cancel', event => { event.preventDefault(); this.closeGallery(); });
      this.on(this.dialog, 'close', () => this.release());
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeGallery();
      });
      this.on(this.dialog, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.showImage(this.imageIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.on(window, 'pagehide', () => this.closeGallery());
      this.on(window, 'resize', () => this.updateTrack(), { passive: true });
      // Shopify replaces section elements on editor reload; custom-element lifecycle owns cleanup.
      this.on(document, 'shopify:section:unload', event => { if (event.target.contains(this)) this.closeGallery(); });
      if (window.ResizeObserver) { this.resizeObserver = new ResizeObserver(() => this.updateTrack()); this.resizeObserver.observe(this.track); }
      this.selectGroup(this.groupButtons[0]?.dataset.frhGroup);
    }

    disconnectedCallback() {
      this.closeGallery(); this.abort?.abort(); this.abort = null; this.resizeObserver?.disconnect();
    }

    on(node, name, callback, options = {}) { node.addEventListener(name, callback, { ...options, signal: this.abort.signal }); }

    selectGroup(group) {
      if (!this.groupButtons.some(button => button.dataset.frhGroup === group)) return;
      this.querySelectorAll('[data-frh-card]').forEach(card => { card.closest('.frh__item').hidden = card.dataset.reviewGroup !== group; });
      this.groupButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.frhGroup === group)));
      const selectedButton = this.groupButtons.find(button => button.dataset.frhGroup === group);
      const label = (selectedButton.querySelector('[data-frh-group-name]') || selectedButton).textContent;
      this.querySelector('[data-frh-category-label]').textContent = label;
      this.track.setAttribute('aria-label', 'Selected ' + label + ' reviews');
      this.track.scrollLeft = 0; this.updateTrack();
    }

    moveTrack(direction) {
      const card = this.track.querySelector('.frh__item:not([hidden])');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      if (step <= 0) return;
      const count = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: direction * step * count, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    updateTrack() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const left = Math.max(0, Math.min(max, this.track.scrollLeft));
      this.previous.disabled = left <= 2;
      this.next.disabled = left >= max - 2;
      if (!this.range) return;
      const items = [...this.track.querySelectorAll('.frh__item:not([hidden])')];
      const total = items.length;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = (items[0]?.getBoundingClientRect().width || 0) + gap;
      if (!total || step <= 0 || !this.track.clientWidth) {
        this.range.textContent = total + (total === 1 ? ' review' : ' reviews');
        return;
      }
      const first = Math.min(total, Math.floor((left + 2) / step) + 1);
      const last = Math.min(total, Math.max(first, Math.ceil((left + this.track.clientWidth - 2) / step)));
      const label = 'Reviews ' + first + (last > first ? '–' + last : '') + ' of ' + total;
      if (this.range.textContent !== label) this.range.textContent = label;
    }

    openGallery(opener) {
      if (!this.dialog.showModal || this.dialog.open || document.querySelector('dialog[open]')) return false;
      const card = opener.closest('[data-frh-card]');
      this.photos = [...card.querySelectorAll('[data-frh-source]')].map(link => ({
        src: link.href, alt: link.dataset.alt, width: Number(link.dataset.width), height: Number(link.dataset.height)
      }));
      if (!this.photos.length) return false;
      this.opener = opener;
      this.querySelector('[data-frh-gallery-name]').textContent = 'From ' + card.querySelector('[data-frh-name]').textContent;
      this.querySelector('[data-frh-gallery-title]').textContent = card.querySelector('.frh__title').textContent;
      this.querySelector('[data-frh-gallery-text]').textContent = card.querySelector('[data-frh-full-text]').textContent;
      this.querySelector('[data-frh-gallery-review]').open = false;
      const product = card.querySelector('.frh__product');
      const galleryProduct = this.querySelector('[data-frh-gallery-product]');
      galleryProduct.textContent = product.textContent;
      galleryProduct.href = product.href;
      this.showImage(0);
      this.ownsLock = !document.documentElement.classList.contains('frh-media-lock');
      document.documentElement.classList.add('frh-media-lock');
      try {
        this.dialog.showModal(); this.querySelector('[data-frh-close]').focus({ preventScroll: true });
        return true;
      } catch (_) { this.release(); return false; }
    }

    showImage(index) {
      if (!this.photos?.length) return;
      this.imageIndex = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[this.imageIndex];
      this.image.width = photo.width; this.image.height = photo.height;
      this.image.src = photo.src; this.image.alt = photo.alt;
      this.querySelector('[data-frh-position]').textContent = 'Photo ' + (this.imageIndex + 1) + ' of ' + this.photos.length;
      this.querySelector('[data-frh-image-prev]').hidden = this.photos.length < 2;
      this.querySelector('[data-frh-image-next]').hidden = this.photos.length < 2;
    }

    closeGallery() { if (this.dialog?.open) this.dialog.close(); this.release(); }

    release() {
      // Never modify body styles or another component's scroll-lock class.
      if (this.ownsLock) document.documentElement.classList.remove('frh-media-lock');
      this.ownsLock = false;
      if (this.opener?.isConnected && !document.querySelector('dialog[open]')) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  customElements.define('fadlash-review-highlights', FadlashReviewHighlights);
})();
